/**
 * "My day" (S-A02) — the vertical slice proving the whole stack.
 *
 * One thing about this screen is a domain rule rather than a layout choice: every
 * figure obeys the reader's scope, and the scoping happens INSIDE the aggregation on
 * the server, not by hiding widgets here (CR-29). A lawyer seeing "revenue by
 * service" could otherwise infer colleagues' matters from the aggregate.
 *
 * The consequence for this component: a metric absent from the payload is a metric
 * the reader has no permission for. Render what arrives; never substitute a zero for
 * something the server did not send, because "0 overdue tasks" and "you cannot see
 * overdue tasks" are different statements and only one of them is true.
 */

import { qk, useApiQuery, type StaffDashboard } from '@legalcrm/api';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusPill,
  cn,
  statusTone,
} from '@legalcrm/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { formatDateTime, formatRelative, humanise, isPast } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

const TONES = {
  neutral: 'text-ink',
  positive: 'text-success',
  warning: 'text-warning',
  critical: 'text-danger',
} as const;

export function DashboardPage() {
  const { me } = useAuth();
  const { data, isLoading, error, refetch } = useApiQuery<StaffDashboard>(
    qk.dashboard(),
    '/dashboard',
  );

  const firstName = me?.user.full_name?.split(' ')[0] ?? '';

  return (
    <>
      <PageHeader
        title={firstName ? `Good day, ${firstName}` : 'My day'}
        description="What needs you today, in the order it needs you."
      />

      {isLoading && <DashboardSkeleton />}

      {!isLoading && error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {!isLoading && !error && data && (
        <div className="flex flex-col gap-6">
          <section aria-label="Summary">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {data.metrics.map((metric) => {
                const tile = (
                  <Card
                    key={metric.key}
                    className={cn(
                      'h-full transition-colors',
                      metric.href && 'hover:border-primary/40',
                    )}
                  >
                    <CardBody className="flex flex-col gap-1">
                      <p className="text-sm text-ink-muted">{metric.label}</p>
                      <p
                        className={cn(
                          'text-3xl font-semibold tabular-nums',
                          TONES[metric.tone ?? 'neutral'],
                        )}
                      >
                        {metric.value}
                      </p>
                      {metric.caption && (
                        <p className="text-xs text-ink-subtle">{metric.caption}</p>
                      )}
                    </CardBody>
                  </Card>
                );
                return metric.href ? (
                  <Link
                    key={metric.key}
                    to={metric.href}
                    className="rounded-lg focus-visible:outline-2"
                  >
                    {tile}
                  </Link>
                ) : (
                  tile
                );
              })}
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            {data.queues.map((queue) => (
              <Card key={queue.key}>
                <CardHeader
                  title={queue.label}
                  actions={
                    queue.href && (
                      <Link
                        to={queue.href}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        View all{queue.total ? ` (${queue.total})` : ''}
                      </Link>
                    )
                  }
                />
                {queue.items.length === 0 ? (
                  <EmptyState size="sm" title="Nothing outstanding" />
                ) : (
                  <ul className="flex flex-col divide-y">
                    {queue.items.map((item) => {
                      const overdue = isPast(item.due_at);
                      const row = (
                        <div className="flex items-start justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <p className="truncate text-base text-ink">{item.title}</p>
                            {item.subtitle && (
                              <p className="truncate text-xs text-ink-muted">
                                {item.subtitle}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            {item.due_at && (
                              <>
                                {/* Relative distance is what the reader judges, but
                                    the absolute date is always beside it: a
                                    limitation date shown only as "in 2 days" is not
                                    a date anyone can act on. */}
                                <p
                                  className={cn(
                                    'text-sm font-medium',
                                    overdue ? 'text-danger' : 'text-ink',
                                  )}
                                >
                                  {formatRelative(item.due_at)}
                                </p>
                                <p className="text-xs text-ink-subtle">
                                  {formatDateTime(item.due_at)}
                                </p>
                              </>
                            )}
                            {item.status && !item.due_at && (
                              <StatusPill
                                label={humanise(item.status)}
                                tone={statusTone(item.status)}
                              />
                            )}
                          </div>
                        </div>
                      );
                      return (
                        <li key={item.id}>
                          {item.href ? (
                            <Link to={item.href} className="block hover:bg-surface-sunken">
                              {row}
                            </Link>
                          ) : (
                            row
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            ))}
          </div>

          <p className="text-xs text-ink-subtle">
            Figures reflect what you are permitted to see. Generated{' '}
            {formatDateTime(data.generated_at)}.
          </p>
        </div>
      )}
    </>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <span className="sr-only">Loading your dashboard</span>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Card key={index}>
            <CardBody className="flex flex-col gap-2">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-8 w-16" />
            </CardBody>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }, (_, index) => (
          <Card key={index}>
            <CardBody className="flex flex-col gap-3">
              {Array.from({ length: 3 }, (_, row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
